import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ApiService } from '@frontend/sports/common/api-utils';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { Action, AlertEvent, Subscription, Subscriptions, Type } from './model';
import { SubscriptionStreamService } from './subscription-stream.service';

@Injectable({ providedIn: 'root' })
export class AlertSubscriptionService {
    private _subscriptions$ = new BehaviorSubject<Subscriptions>({});

    readonly subscriptions$ = this._subscriptions$.asObservable();

    private readonly apiService = inject(ApiService);
    private readonly streamService = inject(SubscriptionStreamService);
    private readonly trackingService = inject(TrackingService);
    private readonly loggerFactory = inject(LoggerFactory);
    private readonly productUtils = inject(SportsProductUtils);
    private readonly logger = this.loggerFactory.getLogger('AlertSubscriptionService');

    constructor() {
        this.streamService.data$.pipe(this.productUtils.filterSportsEmitLast(), takeUntilDestroyed()).subscribe(this.processData);
    }

    private processData = (data: Subscription[]) => {
        const subscriptions: Record<string, Subscription> = {};
        if (data) {
            data.forEach((item) => {
                subscriptions[item.event.id] = item;
            });
        }

        this.propagate(subscriptions);
    };

    private sendSubscriptions(subscriptionsToUpdate: Subscription[]): Promise<void> {
        return firstValueFrom(this.apiService.post<void>('nativealerts/updateSubscriptions', subscriptionsToUpdate));
    }

    updateSubscriptions(subscriptionsToUpdate: Subscription[]): Promise<Subscription[]> {
        subscriptionsToUpdate.forEach((subscription) => {
            subscription.action = subscription.scores && subscription.scores.length > 0 ? Action.Subscribe : Action.Unsubscribe;
        });

        this.sendSubscriptions(subscriptionsToUpdate).catch((error) => {
            if (error.errorCode === 'Unauthenticated') {
                this.streamService.stopGettingSubscriptions();
            } else {
                this.logger.error(error, 'Update Native Alerts Subscriptions failed.');
            }
        });

        return this.updateLocalCollection(subscriptionsToUpdate);
    }

    private updateLocalCollection = (subscriptionsToUpdate: Subscription[]): Promise<Subscription[]> => {
        return new Promise((resolve) => {
            const subscriptions = this._subscriptions$.getValue();
            subscriptionsToUpdate.forEach((newSubscription) => {
                if (newSubscription.action === Action.Subscribe) {
                    this.trackSubscription(newSubscription);
                    subscriptions[newSubscription.event.id as unknown as number] = newSubscription;
                } else {
                    delete subscriptions[newSubscription.event.id as unknown as number];
                }
            });
            this.propagate(subscriptions);
            resolve(subscriptionsToUpdate);
        });
    };

    private trackSubscription(item: Subscription): void {
        if (item.tracking) {
            this.trackingService.track(trackingConstants.EVENT_ALERT_ENABLED, {
                [trackingConstants.PAGE_NAME]: `${item.tracking}_E${item.event.id}_S${item.sportId}`,
            });
            delete item.tracking;
        }
    }

    private propagate(newSubscriptions: Subscriptions): void {
        this._subscriptions$.next(newSubscriptions);
    }

    unsubscribeAll(subscriptionsToRemove: Subscription[]): Promise<Subscription[]> {
        subscriptionsToRemove.forEach((subscription) => {
            subscription.scores = [];
            subscription.action = Action.Unsubscribe;
        });

        this.sendSubscriptions(subscriptionsToRemove);

        return this.updateLocalCollection(subscriptionsToRemove);
    }

    private getSubscriptionForEvent(id: string): Subscription | undefined {
        return this._subscriptions$.getValue()[id as unknown as number];
    }

    private getSubscriptionForGroup(groupId?: number): Subscription | undefined {
        let subscription: Subscription | undefined;
        const currentSubscriptions = this._subscriptions$.getValue();

        Object.keys(currentSubscriptions).forEach((key) => {
            if (currentSubscriptions[key as unknown as number].event.groupId === groupId) {
                subscription = currentSubscriptions[key as unknown as number];
            }
        });

        return subscription;
    }

    getSubscription(id: string, groupId?: number): Subscription | undefined {
        let subscription: Subscription | undefined;

        subscription = this.getSubscriptionForEvent(id);
        if (!subscription && groupId && groupId > 0) {
            subscription = this.getSubscriptionForGroup(groupId);
        }

        return subscription;
    }

    getSubscriptions(events: AlertEvent[]): Subscription[] {
        return events.map((e) => this.getSubscription(e.eventId, e.eventGroupId)).filter(isDefined);
    }

    createNewSubscription(alertEvent: AlertEvent): Subscription {
        return {
            action: Action.Subscribe,
            event: {
                id: alertEvent.eventId,
                groupId: alertEvent.eventGroupId,
                idType: alertEvent.live ? Type.Live : Type.NonLive,
            },
            league: {
                id: alertEvent.leagueId.toString(),
                groupId: alertEvent.leagueGroupId,
                idType: Type.NonLive, // hardcoded per instructions from CNS team
            },
            scores: [],
            sportId: alertEvent.sportId,
        };
    }
}
